import { useMemo } from 'react';
import { RadixSelect } from '@/components/ui';

/**
 * ID36: 슬롯 시간 셀렉트. 분(minute)을 5분 단위(00,05,…,55)로만 선택하게 강제한다.
 * BE 가 minute%5==0 && second==0 을 검증하므로, FE 도 동일 제약을 UI 로 막아
 * 400 을 사전 차단한다. 값/onChange 는 "HH:mm" 문자열로 주고받는다.
 *
 * 과거 데이터가 5분 단위가 아니면(예: "10:03") 옵션에 없는 값이 들어올 수 있으므로,
 * 현재 값이 옵션에 없을 때 그 값을 1회성 옵션으로 추가해 select 가 빈 값으로
 * 보이지 않게 한다(수정 시 사용자가 명시적으로 5분 단위를 다시 고르게 됨).
 */

const pad2 = (n: number): string => String(n).padStart(2, '0');

const buildOptions = (): ReadonlyArray<{ value: string; label: string }> => {
  const opts: Array<{ value: string; label: string }> = [];
  for (let h = 0; h < 24; h += 1) {
    for (let m = 0; m < 60; m += 5) {
      const v = `${pad2(h)}:${pad2(m)}`;
      opts.push({ value: v, label: v });
    }
  }
  return opts;
};

const FIVE_MIN_OPTIONS = buildOptions();

export interface TimeSelect5Props {
  value: string;
  onChange: (value: string) => void;
  ariaLabel: string;
  className?: string;
  placeholder?: string;
}

export const TimeSelect5 = ({ value, onChange, ariaLabel, className, placeholder = '--:--' }: TimeSelect5Props) => {
  const options = useMemo(() => {
    // 5분 단위가 아닌 기존 값(과거 데이터) 보존 — 옵션에 없으면 1회성으로 추가.
    if (value !== '' && !FIVE_MIN_OPTIONS.some((o) => o.value === value)) {
      return [{ value, label: value }, ...FIVE_MIN_OPTIONS];
    }
    return FIVE_MIN_OPTIONS;
  }, [value]);

  return (
    <RadixSelect
      value={value}
      onChange={onChange}
      options={options}
      aria-label={ariaLabel}
      placeholder={placeholder}
      // exactOptionalPropertyTypes: className 이 undefined 면 prop 자체를 생략.
      {...(className !== undefined ? { className } : {})}
    />
  );
};
